<script lang="ts">
  import {
    createProject as apiCreateProject,
    deleteProject,
    patchProject,
    getErrorMessage
  } from '$lib/api';
  import {
    Plus,
    FolderOpen,
    CheckCircle2,
    ChevronRight,
    Building2,
    Cpu,
    Gamepad2,
    Zap,
    Circle,
    Eye,
    Trash2,
    Pencil,
    Search
  } from 'lucide-svelte';
  import ConfirmDialog from './ui/ConfirmDialog.svelte';
  import Modal from './ui/Modal.svelte';
  import SkeletonCard from './ui/SkeletonCard.svelte';
  import { projectsQuery } from '$lib/queries/projects';
  import { allUnitsQuery } from '$lib/queries/units';
  import { setLoading, setError, clearError, uiState } from '$lib/state/ui.svelte';
  import { showSuccess, showError } from '$lib/state/toast.svelte';
  import { invalidateAfterProjectUpdate } from '$lib/state/invalidation';
  import type { Project } from '$lib/types/project';

  let {
    openProject = (_p: Project) => {}
  }: {
    openProject?: (project: Project) => void;
  } = $props();

  const projectsQ = projectsQuery();
  const unitsQ = allUnitsQuery();

  let projects = $derived(projectsQ.data ?? []);
  let allUnits = $derived(unitsQ.data ?? []);
  let isProjectsLoading = $derived(projectsQ.isLoading);

  let showCreateProject = $state(false);
  let newProject = $state({ name: '', description: '' });
  let searchTerm = $state('');
  let filterStatus = $state('all');
  let showDeleteProjectConfirm = $state(false);
  let projectToDelete = $state<Project | null>(null);

  let editingProjectId = $state<number | null>(null);
  let editingName = $state('');
  let renamingProjectId = $state<number | null>(null);

  function focusOnMount(node: HTMLInputElement) {
    node.focus();
    node.select();
  }

  function startEditingProjectName(project: Project) {
    editingProjectId = project.id;
    editingName = project.name;
  }

  function cancelEditingProjectName() {
    editingProjectId = null;
    editingName = '';
  }

  async function saveProjectName(project: Project) {
    const name = editingName.trim();
    editingProjectId = null;
    if (!name || name === project.name) return;

    renamingProjectId = project.id;
    try {
      await patchProject(project.id, { name });
      invalidateAfterProjectUpdate();
    } catch (error) {
      showError('Rename failed: ' + getErrorMessage(error));
    } finally {
      renamingProjectId = null;
    }
  }

  function unitCountForProject(projectId: number) {
    return allUnits.filter((u) => u.project === projectId).length;
  }

  function getProjectStatus(projectId: number) {
    return unitCountForProject(projectId) === 0 ? 'setup' : 'complete';
  }

  async function createProject() {
    if (!newProject.name.trim()) return;

    setLoading(true);
    clearError();

    try {
      const project = await apiCreateProject(newProject);
      invalidateAfterProjectUpdate();
      newProject = { name: '', description: '' };
      showCreateProject = false;
      openProject(project);
    } catch (error: unknown) {
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  function confirmDeleteProject(project: Project) {
    projectToDelete = project;
    showDeleteProjectConfirm = true;
  }

  async function handleDeleteProject() {
    showDeleteProjectConfirm = false;
    if (!projectToDelete) return;
    const projectId = projectToDelete.id;
    projectToDelete = null;

    setLoading(true);
    clearError();

    try {
      await deleteProject(projectId);
      invalidateAfterProjectUpdate();
      showSuccess('Project deleted successfully');
    } catch (error: unknown) {
      showError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  let filteredProjects = $derived(
    projects.filter((project) => {
      const matchesSearch =
        project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filterStatus === 'all' || getProjectStatus(project.id) === filterStatus;
      return matchesSearch && matchesFilter;
    })
  );

  const statusConfig: Record<string, { label: string; color: string; icon: typeof Circle }> = {
    setup: { label: 'Setup', color: 'bg-bg-hover text-text-primary', icon: Circle },
    complete: { label: 'Complete', color: 'bg-bg-hover text-text-secondary', icon: CheckCircle2 }
  };
</script>

<div class="p-6">
  <!-- Header -->
  <div class="mb-8">
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-text-primary">Projects Dashboard</h1>
        <p class="text-text-secondary mt-1">Manage your RCU configuration projects</p>
      </div>
      <button
        onclick={() => (showCreateProject = true)}
        class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-accent-on-primary bg-accent-primary hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-primary transition-all"
      >
        <Plus class="w-4 h-4 mr-2" />
        New Project
      </button>
    </div>
  </div>

  <!-- Search and Filter -->
  <div class="bg-bg-canvas rounded-lg shadow-sm border border-border-default p-4 mb-6">
    <div class="flex flex-col sm:flex-row gap-4">
      <div class="flex-1">
        <div class="relative">
          <Search
            class="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted w-4 h-4"
          />
          <input
            type="text"
            bind:value={searchTerm}
            placeholder="Search projects..."
            class="w-full pl-10 pr-4 py-2 border border-border-strong rounded-md focus:ring-accent-primary focus:border-accent-primary"
          />
        </div>
      </div>
      <div class="flex items-center space-x-4">
        <select
          bind:value={filterStatus}
          class="px-3 py-2 border border-border-strong rounded-md focus:ring-accent-primary focus:border-accent-primary"
        >
          <option value="all">All Status</option>
          <option value="setup">Setup</option>
          <option value="complete">Complete</option>
        </select>
      </div>
    </div>
  </div>

  <!-- Projects Grid -->
  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {#if isProjectsLoading}
      {#each Array(6) as _}
        <SkeletonCard />
      {/each}
    {:else}
      {#each filteredProjects as project}
        {@const unitCount = unitCountForProject(project.id)}
        {@const status = getProjectStatus(project.id)}
        {@const statusInfo = statusConfig[status]}
        {@const StatusIcon = statusInfo.icon}

        <div
          class="bg-bg-canvas rounded-lg shadow-sm border border-border-default hover:shadow-md transition-shadow duration-200"
        >
          <div class="p-6">
            <!-- Project Header -->
            <div class="flex items-start justify-between mb-4">
              <div class="flex-1 min-w-0">
                {#if editingProjectId === project.id}
                  <input
                    type="text"
                    bind:value={editingName}
                    use:focusOnMount
                    onblur={() => saveProjectName(project)}
                    onkeydown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        (e.currentTarget as HTMLInputElement).blur();
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        cancelEditingProjectName();
                      }
                    }}
                    class="w-full text-lg font-semibold text-text-primary mb-1 border border-accent-primary rounded px-1 -mx-1 focus:outline-none focus:ring-2 focus:ring-accent-primary"
                  />
                {:else}
                  <button
                    type="button"
                    onclick={() => startEditingProjectName(project)}
                    class="group flex items-center gap-1.5 text-lg font-semibold text-text-primary mb-1 text-left hover:text-accent-primary"
                    title="Click to rename"
                  >
                    <span class="truncate">{project.name}</span>
                    {#if renamingProjectId === project.id}
                      <span class="text-xs text-text-muted">Saving…</span>
                    {:else}
                      <Pencil
                        class="w-3.5 h-3.5 text-text-muted opacity-0 group-hover:opacity-100 shrink-0"
                      />
                    {/if}
                  </button>
                {/if}
                <p class="text-sm text-text-secondary line-clamp-2">{project.description}</p>
              </div>
              <div class="flex items-center space-x-2 ml-4">
                <button
                  onclick={() => openProject(project)}
                  class="p-1 text-text-muted hover:text-text-secondary rounded"
                  title="Open Project"
                  aria-label="View project"
                >
                  <Eye class="w-4 h-4" />
                </button>
                <button
                  onclick={() => confirmDeleteProject(project)}
                  class="p-1 text-text-muted hover:text-state-error rounded"
                  title="Delete Project"
                  aria-label="Delete project"
                >
                  <Trash2 class="w-4 h-4" />
                </button>
              </div>
            </div>

            <!-- Status Badge -->
            <div class="mb-4">
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium {statusInfo.color}"
              >
                <StatusIcon class="w-3 h-3 mr-1" />
                {statusInfo.label}
              </span>
            </div>

            <!-- Stats -->
            <div class="mb-4">
              <div class="text-center m-auto">
                <p class="text-2xl font-bold text-text-primary">{unitCount}</p>
                <p class="text-xs text-text-muted">Units</p>
              </div>
            </div>

            <!-- Action Button -->
            <button
              onclick={() => openProject(project)}
              class="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-bg-base bg-text-primary hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-primary transition-opacity"
            >
              Open Project
              <ChevronRight class="ml-2 w-4 h-4" />
            </button>
          </div>
        </div>
      {/each}
    {/if}
  </div>

  <!-- Empty State -->
  {#if filteredProjects.length === 0 && projects.length > 0}
    <div class="text-center py-12">
      <FolderOpen class="mx-auto h-12 w-12 text-text-muted" />
      <h3 class="mt-2 text-sm font-medium text-text-primary">No projects found</h3>
      <p class="mt-1 text-sm text-text-muted">Try adjusting your search or filter criteria.</p>
    </div>
  {/if}

  {#if projects.length === 0 && !isProjectsLoading}
    <div class="text-center py-12">
      <FolderOpen class="mx-auto h-12 w-12 text-text-muted" />
      <h3 class="mt-2 text-sm font-medium text-text-primary">No projects yet</h3>
      <p class="mt-1 text-sm text-text-muted">Get started by creating your first project.</p>
      <div class="mt-6">
        <button
          onclick={() => (showCreateProject = true)}
          class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-accent-on-primary bg-accent-primary hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-primary transition-all"
        >
          <Plus class="w-4 h-4 mr-2" />
          Create Project
        </button>
      </div>
    </div>
  {/if}
</div>

<!-- Create Project Modal -->
<Modal open={showCreateProject} size="md" onclose={() => (showCreateProject = false)}>
    {#snippet header()}
      <h3 class="text-lg font-medium text-text-primary">Create New Project</h3>
    {/snippet}
    {#snippet children()}
      <div class="space-y-4">
        <div>
          <label for="project-name" class="block text-sm font-medium text-text-secondary"
            >Project Name</label
          >
          <input
            id="project-name"
            type="text"
            bind:value={newProject.name}
            placeholder="Enter project name"
            class="mt-1 block w-full border border-border-strong rounded-md px-3 py-2 focus:outline-none focus:ring-accent-primary focus:border-accent-primary"
          />
        </div>

        <div>
          <label for="project-description" class="block text-sm font-medium text-text-secondary"
            >Description</label
          >
          <textarea
            id="project-description"
            bind:value={newProject.description}
            placeholder="Enter project description"
            rows="3"
            class="mt-1 block w-full border border-border-strong rounded-md px-3 py-2 focus:outline-none focus:ring-accent-primary focus:border-accent-primary"
          ></textarea>
        </div>
      </div>
    {/snippet}
    {#snippet footer()}
      <div class="flex justify-end space-x-3">
        <button
          onclick={() => (showCreateProject = false)}
          class="px-4 py-2 text-sm font-medium text-text-secondary bg-bg-hover hover:bg-bg-hover rounded-md"
        >
          Cancel
        </button>
        <button
          onclick={createProject}
          disabled={!newProject.name.trim() || uiState.isLoading}
          class="px-4 py-2 text-sm font-medium text-accent-on-primary bg-accent-primary hover:opacity-90 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {#if uiState.isLoading}Creating...{:else}Create Project{/if}
        </button>
      </div>
    {/snippet}
  </Modal>

<ConfirmDialog
  bind:open={showDeleteProjectConfirm}
  title="Delete Project"
  message="Are you sure you want to delete this project? This action cannot be undone."
  confirmText="Delete"
  variant="danger"
  on:confirm={handleDeleteProject}
/>
